import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import { User, Mail, Phone, MapPin, Briefcase, Camera, Award, Save, Edit } from "lucide-react";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../../store/useAuthStore";
import useProfileStore from "../../store/builders/useProfileStore";
import toast from "react-hot-toast";

const BuilderConsultantProfile = ({ excludeLayout = false }) => {
    const navigate = useNavigate();
    const { token, user } = useAuthStore();
    const { profile: builderProfile, fetchProfile: fetchBuilderProfile, updateProfile: updateBuilderProfile } = useProfileStore();
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [hasProfile, setHasProfile] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [activeTab, setActiveTab] = useState("profile");
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone_number: "",
        district: "",
        taluk: "",
        village_name: "",
        pincode: "",
        qualification: "",
        experience: ""
    });

    useEffect(() => {
        // Ensure we have builder profile data for prefilling (engineer name, email, phone, location, profile image)
        fetchBuilderProfile();
        fetchBuilderConsultantProfile();
    }, []);

    // If builder profile loads after the page, gently prefill any empty fields (only when consultant profile doesn't exist yet)
    useEffect(() => {
        if (!hasProfile) {
            setFormData(prev => prefillFromBuilderProfile(prev));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [builderProfile]);

    const prefillFromBuilderProfile = (current) => {
        const bp = builderProfile?.builder || builderProfile;
        if (!bp) return current;

        // Compute experience from establishment year, if present
        let experienceFromEst = current.experience;
        if (!experienceFromEst && bp.establishment_year) {
            const currentYear = new Date().getFullYear();
            const estYear = parseInt(bp.establishment_year, 10);
            if (!isNaN(estYear)) {
                experienceFromEst = Math.max(0, currentYear - estYear).toString();
            }
        }

        return {
            ...current,
            name: current.name || bp.engineer_name || bp.company_name || "",
            email: current.email || bp.email || "",
            phone_number: current.phone_number || bp.contact_number || bp.whatsapp_number || bp.user_account_phone || "",
            district: current.district || bp.district || "",
            taluk: current.taluk || bp.taluk || "",
            village_name: current.village_name || bp.village || "",
            pincode: current.pincode || bp.postcode || "",
            qualification: current.qualification || bp.qualification || "",
            experience: experienceFromEst || current.experience || bp.experience || ""
        };
    };

    const fetchBuilderConsultantProfile = async () => {
        try {
            setLoading(true);
            const response = await axiosInstancev1.get("/builder/consultant-profile", {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.profile) {
                setHasProfile(true);
                setProfileData(response.data.profile);
                setFormData({
                    name: response.data.profile.name || "",
                    email: response.data.profile.email || builderProfile?.email || "",
                    phone_number: response.data.profile.phone_number || "",
                    district: response.data.profile.district || "",
                    taluk: response.data.profile.taluk || "",
                    village_name: response.data.profile.village_name || "",
                    pincode: response.data.profile.pincode || "",
                    qualification: response.data.profile.qualification || "",
                    experience: response.data.profile.experience || ""
                });
            } else {
                setHasProfile(false);
                // Pre-fill from builder profile if available (preferred), otherwise from auth user
                setFormData(prev => {
                    let next = prefillFromBuilderProfile(prev);
                    if (user) {
                        next = {
                            ...next,
                            name: next.name || user.name || "",
                            email: next.email || user.email || "",
                            phone_number: next.phone_number || user.phone || user.contactNumber || ""
                        };
                    }
                    return next;
                });
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setHasProfile(false);
                setFormData(prev => {
                    let next = prefillFromBuilderProfile(prev);
                    if (user) {
                        next = {
                            ...next,
                            name: next.name || user.name || "",
                            email: next.email || user.email || "",
                            phone_number: next.phone_number || user.phone || user.contactNumber || ""
                        };
                    }
                    return next;
                });
            } else {
                console.error("Error fetching consultant profile:", error);
                toast.error("Failed to load profile");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formDataObj = new FormData();
        formDataObj.append("profile_image", file);

        try {
            const response = await axiosInstancev1.post("/builder/consultant-profile/upload-image", formDataObj, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                }
            });

            if (response.data.success) {
                toast.success("Image uploaded successfully");
                fetchBuilderConsultantProfile();
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            toast.error("Failed to upload image");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.name || !formData.phone_number || !formData.district || !formData.qualification || !formData.experience) {
            toast.error("Please fill all required fields");
            return;
        }

        try {
            // If email changed, update it in builder profile (users.email) so it stays consistent everywhere.
            // This does NOT change consultant profile table (there is no email column there); it updates the account email.
            const currentEmail = (builderProfile?.email || builderProfile?.builder?.email || user?.email || "").trim();
            const nextEmail = (formData.email || "").trim();
            if (nextEmail && nextEmail !== currentEmail) {
                const result = await updateBuilderProfile({ email: nextEmail });
                if (!result?.success) {
                    toast.error(result?.message || "Failed to update email");
                    return;
                }
            }

            const endpoint = hasProfile ? "/builder/consultant-profile/update" : "/builder/consultant-profile/create";
            const payload = { ...formData };
            delete payload.email; // backend doesn't accept email in this endpoint
            const response = await axiosInstancev1.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success(hasProfile ? "Profile updated successfully" : "Profile created successfully");
                setIsEditing(false);
                fetchBuilderConsultantProfile();

                // If new profile, redirect to subscription
                if (!hasProfile) {
                    setTimeout(() => {
                        navigate("/builder/consultant-profile-subscription");
                    }, 1500);
                }
            }
        } catch (error) {
            console.error("Error saving profile:", error);
            toast.error(error.response?.data?.message || "Failed to save profile");
        }
    };

    // Consultant profile image is managed separately from builder profile image.
    // If consultant hasn't uploaded yet, show a neutral placeholder.
    const displayImage = profileData?.profile_image || "/assets/Support.png";
    const completionPercentage = calculateCompletion();

    function calculateCompletion() {
        const fields = Object.values(formData).filter(Boolean).length;
        const total = Object.keys(formData).length;
        return Math.round((fields / total) * 100);
    }

    if (loading) {
        if (excludeLayout) {
            return (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
            );
        }
        return (
            <Layout>
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
            </Layout>
        );
    }

    const tabs = [
        { id: "profile", label: "Profile" },
        { id: "address", label: "Address" },
    ];

    const Content = (
        <div className="space-y-4 pb-4">
            {!excludeLayout && (
                /* Header Card */
                <div className="bg-white shadow-sm rounded-xl text-sm border border-gray-100 overflow-hidden">
                    <div className="px-5 py-5">
                        <div className="flex items-center justify-between mb-4">
                            <h1 className="text-base sm:text-lg font-bold text-gray-900">
                                Consultant Profile
                            </h1>
                            {hasProfile && profileData?.subscription_status === 'active' && (
                                <span className="px-3 py-1 bg-green-100 text-green-700 text-[11px] font-semibold rounded-full">
                                    Active Listing
                                </span>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-5 text-center sm:text-left">
                            <div className="relative">
                                <img
                                    src={displayImage}
                                    alt="Profile"
                                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border border-gray-200 shadow-sm object-cover bg-gray-50"
                                    onError={(e) => { e.target.src = "/assets/Support.png"; }}
                                />
                                {hasProfile && (
                                    <label className="absolute -top-1 -right-1 bg-white rounded-full p-1.5 shadow-md cursor-pointer hover:bg-gray-100 border border-gray-100">
                                        <Camera size={12} className="text-gray-600" />
                                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                    </label>
                                )}
                            </div>

                            <div className="flex-1 w-full sm:w-auto">
                                <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                                    {formData.name || "Consultant"}
                                </h2>
                                <p className="text-gray-500 text-[10px] sm:text-xs flex flex-wrap items-center justify-center sm:justify-start gap-1.5 sm:gap-2 mt-0.5">
                                    <span className="truncate max-w-[150px] sm:max-w-none">{formData.qualification || "Qualification"}</span>
                                    <span className="text-gray-300 hidden sm:inline">•</span>
                                    <span>{formData.district || "District"}</span>
                                </p>
                                <div className="mt-3 flex flex-col items-center sm:items-start">
                                    <div className="h-1.5 bg-gray-100 rounded-full w-32 sm:w-48 overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 rounded-full transition-all"
                                            style={{ width: `${completionPercentage || 0}%` }}
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1 font-medium">
                                        {completionPercentage || 0}%
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab Navigation - Old Style */}
                    <div className="bg-white border-t border-gray-50 px-2 pt-2">
                        <ul className="flex justify-start gap-2 text-gray-600 w-full px-2 text-sm overflow-x-auto no-scrollbar">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    className={`cursor-pointer px-5 py-2.5 transition-all duration-200 select-none whitespace-nowrap text-[13px] font-medium ${activeTab === tab.id ? "bg-[#E0E0E0] text-gray-900 font-bold" : "hover:bg-gray-50 hover:text-gray-900"
                                        }`}
                                    style={{ borderRadius: "10px 10px 0 0" }}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {excludeLayout && (
                /* Compact Profile Info for Tabbed View */
                <div className="bg-white shadow-sm rounded-xl p-4 border border-gray-100 flex items-center gap-4">
                    <div className="relative">
                        <img
                            src={displayImage}
                            alt="Profile"
                            className="w-12 h-12 rounded-full border border-gray-200 object-cover bg-gray-50"
                            onError={(e) => { e.target.src = "/assets/Support.png"; }}
                        />
                        {hasProfile && (
                            <label className="absolute -top-1 -right-1 bg-white rounded-full p-1.5 shadow-md cursor-pointer hover:bg-gray-100 border border-gray-100">
                                <Camera size={10} className="text-gray-600" />
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                            </label>
                        )}
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-gray-900">{formData.name || "Consultant"}</h2>
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-500">{formData.qualification}</span>
                            <span className="h-1 w-1 rounded-full bg-gray-300" />
                            <span className="text-[10px] text-gray-500">{formData.district}</span>
                        </div>
                    </div>
                    {hasProfile && profileData?.subscription_status === 'active' && (
                        <div className="ml-auto">
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold rounded-full uppercase tracking-wider">
                                Active Listing
                            </span>
                        </div>
                    )}
                </div>
            )}

            {excludeLayout && (
                /* Tab Navigation for Tabbed View - Old Style */
                <div className="bg-white border border-gray-100 rounded-xl px-2 pt-2 shadow-sm">
                    <ul className="flex justify-start gap-2 text-gray-600 w-full px-2 text-sm overflow-x-auto no-scrollbar">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`cursor-pointer px-5 py-2.5 transition-all duration-200 select-none whitespace-nowrap text-[13px] font-medium ${activeTab === tab.id ? "bg-[#E0E0E0] text-gray-900 font-bold" : "hover:bg-gray-50 hover:text-gray-900"
                                    }`}
                                style={{ borderRadius: "10px 10px 0 0" }}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </ul>
                </div>
            )}

            {/* Simple subscription notice */}
            {hasProfile && profileData?.subscription_status !== 'active' && (
                <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                        <Award className="text-yellow-600" size={16} />
                        <p className="text-yellow-800">
                            Your consultant profile is not visible. Complete subscription to appear in listings.
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            if (excludeLayout) {
                                // If in tabbed view, we'll need a way for parent to change tab.
                                // For now, let's assume parent passes a way or we just use URL.
                                const params = new URLSearchParams(window.location.search);
                                params.set("tab", "subscription");
                                window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);
                                // Trigger a re-render by dispatching a popstate or similar if needed, 
                                // but we'll likely handle this via a prop later if needed.
                                window.dispatchEvent(new PopStateEvent('popstate'));
                            } else {
                                navigate("/builder/consultant-profile-subscription");
                            }
                        }}
                        className="px-3 py-1.5 bg-yellow-500 text-white rounded-md text-[11px] font-semibold hover:bg-yellow-600 transition-colors"
                    >
                        Subscribe
                    </button>
                </div>
            )}

            {/* Content Area */}
            <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
                <div className="p-5">
                    <div className="flex items-center gap-3 mb-6">
                        <h3 className="text-sm font-bold text-gray-900">
                            {activeTab === "profile" ? "My Details" : "Address Details"}
                        </h3>
                        {(!hasProfile || isEditing) ? (
                            <>
                                <button
                                    type="submit"
                                    className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-[11px] font-bold hover:bg-blue-700 transition-all shadow-sm flex items-center gap-2"
                                >
                                    <Save size={14} />
                                    {hasProfile ? "Save" : "Create"}
                                </button>
                                {hasProfile && isEditing && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditing(false);
                                            fetchBuilderConsultantProfile();
                                        }}
                                        className="px-4 py-1.5 bg-gray-100 text-gray-600 rounded-md text-[11px] font-bold hover:bg-gray-200 transition-all font-sans"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-[11px] font-bold hover:bg-blue-700 transition-all shadow-sm flex items-center gap-2"
                            >
                                <Edit size={14} />
                                Edit
                            </button>
                        )}
                    </div>

                    {activeTab === "profile" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Full Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    disabled={hasProfile && !isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${(!hasProfile || isEditing) ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80 text-gray-700"}`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    disabled={hasProfile && !isEditing}
                                    pattern="[0-9]{10}"
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${(!hasProfile || isEditing) ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80 text-gray-700"}`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={hasProfile && !isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${(!hasProfile || isEditing) ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80 text-gray-700"}`}
                                />
                                {isEditing && (
                                    <p className="text-[10px] text-gray-400 mt-1">Updates your account email.</p>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Qualification</label>
                                <input
                                    type="text"
                                    name="qualification"
                                    value={formData.qualification}
                                    onChange={handleInputChange}
                                    disabled
                                    className="w-full px-3 py-2.5 rounded-md bg-gray-100/50 border-gray-100 text-sm text-gray-500 outline-none border transition-all italic"
                                />
                                <p className="text-[10px] text-gray-400 mt-1">From Builder documentation.</p>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Experience (Years)</label>
                                <input
                                    type="number"
                                    name="experience"
                                    value={formData.experience}
                                    onChange={handleInputChange}
                                    disabled
                                    className="w-full px-3 py-2.5 rounded-md bg-gray-100/50 border-gray-100 text-sm text-gray-500 outline-none border transition-all italic"
                                />
                                <p className="text-[10px] text-gray-400 mt-1">Calculated from establishment year.</p>
                            </div>
                        </div>
                    )}

                    {activeTab === "address" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">District</label>
                                <input
                                    type="text"
                                    name="district"
                                    value={formData.district}
                                    onChange={handleInputChange}
                                    disabled={true}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border bg-gray-50/50 border-gray-100 disabled:opacity-80 text-gray-700`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Taluk</label>
                                <input
                                    type="text"
                                    name="taluk"
                                    value={formData.taluk}
                                    onChange={handleInputChange}
                                    disabled={true}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border bg-gray-50/50 border-gray-100 disabled:opacity-80 text-gray-700`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Village Name</label>
                                <input
                                    type="text"
                                    name="village_name"
                                    value={formData.village_name}
                                    onChange={handleInputChange}
                                    disabled={true}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border bg-gray-50/50 border-gray-100 disabled:opacity-80 text-gray-700`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Pincode</label>
                                <input
                                    type="text"
                                    name="pincode"
                                    value={formData.pincode}
                                    onChange={handleInputChange}
                                    disabled={true}
                                    pattern="[0-9]{6}"
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border bg-gray-50/50 border-gray-100 disabled:opacity-80 text-gray-700`}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </form>
        </div>
    );

    if (excludeLayout) return Content;

    return (
        <Layout>
            {Content}
        </Layout>
    );
};

export default BuilderConsultantProfile;
